import type { Capability } from '@gsls/shared';
import { Link } from 'react-router-dom';
import { StickyActionBar } from '../../components/StickyActionBar';
import { useSession } from '../../lib/session';

/**
 * The dashboard's "do the next thing" buttons.
 *
 * On a wide screen they sit under the heading; on a phone the same actions ride
 * a bar pinned to the bottom of the content, within thumb reach. Each is gated
 * on the capability the API will also require, so a workspace never offers an
 * action its holder cannot complete.
 */
export interface QuickAction {
  label: string;
  to: string;
  capability?: Capability;
  tone?: 'primary' | 'outline';
}

const TONES = {
  primary: 'border-transparent bg-brand text-white hover:bg-[color:var(--gs-primary-hover)]',
  outline: 'border-surface-border bg-white text-brand-deep hover:border-brand',
} as const;

function ActionButton({ action }: { action: QuickAction }) {
  return (
    <Link
      to={action.to}
      className={`inline-flex h-11 items-center justify-center rounded-full border px-4 text-xs font-bold uppercase tracking-wide transition md:h-9 ${TONES[action.tone ?? 'outline']}`}
    >
      {action.label}
    </Link>
  );
}

function useAllowed(actions: QuickAction[]): QuickAction[] {
  const { can } = useSession();
  return actions.filter((action) => !action.capability || can(action.capability));
}

/** Inline actions, shown from the `md` breakpoint up. */
export function QuickActions({ actions }: { actions: QuickAction[] }) {
  const allowed = useAllowed(actions);
  if (allowed.length === 0) return null;

  return (
    <div className="hidden flex-wrap gap-2 md:flex">
      {allowed.map((action) => (
        <ActionButton key={action.label} action={action} />
      ))}
    </div>
  );
}

/**
 * The phone bar is the shared `StickyActionBar`; keeping the wrapper means the
 * dashboards can keep passing their own `QuickAction` shape.
 */
export function StickyQuickActions({ actions }: { actions: QuickAction[] }) {
  return (
    <StickyActionBar
      actions={actions.map((action) => ({
        label: action.label,
        to: action.to,
        tone: action.tone,
        ...(action.capability ? { capability: action.capability } : {}),
      }))}
    />
  );
}
